import {
  html,
  type ReactiveController,
  type ReactiveControllerHost,
  type TemplateResult,
} from "lit";
import { FEEDBACK_SUBTYPES, type FeedbackSubtype, type FeedbackWidgetSubmitDetail } from "../types";

interface GeneralFeedbackCallbacks {
  onBack: () => void;
  onCancel: (e: Event) => void;
  onSubmit: (detail: FeedbackWidgetSubmitDetail) => void;
}

export class GeneralFeedbackController implements ReactiveController {
  private _feedbackText = "";
  private _feedbackType = "suggestion";
  private _onFeedbackInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") {
      this._feedbackText = e.detail.value;
      this.host.requestUpdate();
    }
  };

  private _onSubmit = () => {
    this.callbacks.onSubmit({
      feedback: this._feedbackText,
      feedbackType: this._feedbackType,
    });
  };

  private _onTypeChange = (e: CustomEvent<{ value: string }>) => {
    this._feedbackType = e.detail.value;
    this.host.requestUpdate();
  };

  private _pendingFocus = false;

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private callbacks: GeneralFeedbackCallbacks
  ) {
    host.addController(this);
  }

  hostUpdated() {
    if (this._pendingFocus) {
      this._pendingFocus = false;
      this.host.updateComplete.then(() => {
        this.host.shadowRoot?.querySelector<HTMLElement>("eko-textarea")?.focus();
      });
    }
  }

  render(params: {
    errorMessage: string | undefined;
    icon: string;
    loading: boolean;
  }): TemplateResult {
    const { errorMessage, icon, loading } = params;
    const subtype = this._currentSubtype;

    return html`
      <eko-featured-icon
        slot="icon"
        variant="square-modern"
        theme="neutral"
        icon=${icon}></eko-featured-icon>
      <span slot="title" class="eko-feedback-widget__title">Let us know your feedback</span>

      <div class="eko-feedback-widget__form">
        ${errorMessage ? html`<eko-alert variant="danger">${errorMessage}</eko-alert>` : html``}
        <eko-select label="Type of feedback" @change=${this._onTypeChange}>
          ${FEEDBACK_SUBTYPES.map(
            (s) =>
              html`<option value=${s.value} ?selected=${this._feedbackType === s.value}>
                ${s.label}
              </option>`
          )}
        </eko-select>
        <eko-textarea
          label=${subtype.textLabel}
          placeholder=${subtype.placeholder}
          .value=${this._feedbackText}
          .rows=${5}
          ?required=${true}
          @input=${this._onFeedbackInput}></eko-textarea>
      </div>

      <div slot="footer" class="eko-feedback-widget__footer">
        <eko-button ?disabled=${loading} @click=${this.callbacks.onBack}>Back</eko-button>
        <eko-button
          variant="primary"
          ?disabled=${!this._feedbackText || loading}
          ?loading=${loading}
          @click=${this._onSubmit}
          >Submit</eko-button
        >
      </div>
    `;
  }

  requestFocus() {
    this._pendingFocus = true;
  }

  reset() {
    this._feedbackText = "";
    this._feedbackType = "suggestion";
    this._pendingFocus = false;
  }

  private get _currentSubtype(): FeedbackSubtype {
    return FEEDBACK_SUBTYPES.find((s) => s.value === this._feedbackType) ?? FEEDBACK_SUBTYPES[0];
  }
}
